import { useCallback, useEffect, useState } from 'react'
import {
  fetchNotificationCounts,
  type NotificationCounts,
  type NotificationTodoPrompt,
} from '@/lib/api/notificationCountService'
import { useUserRole } from '@/hooks/useUserRole'
import { formatApiError, showWarning } from '@/lib/utils/notifications'

const POLL_INTERVAL_MS = 180_000

export interface UseNotificationCountsResult {
  counts: NotificationCounts | null
  todoPrompt: NotificationTodoPrompt | null
  dismissTodoPrompt: () => void
  refreshCounts: () => Promise<void>
}

/**
 * Polls /api/notification_count for header badge + todo dialog (Angular layout timer).
 */
export function useNotificationCounts(enabled = true): UseNotificationCountsResult {
  const { isLibertyRole } = useUserRole()
  const [counts, setCounts] = useState<NotificationCounts | null>(null)
  const [todoPrompt, setTodoPrompt] = useState<NotificationTodoPrompt | null>(null)

  const refreshCounts = useCallback(async () => {
    try {
      const response = await fetchNotificationCounts()
      setCounts(response)
      if (response.todo?.header && response.todo?.description) {
        setTodoPrompt({
          header: response.todo.header,
          description: response.todo.description,
        })
      }
    } catch (error: unknown) {
      showWarning(formatApiError(error))
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: initial notification-count fetch on mount (data-load effect; also polled on an interval below)
    void refreshCounts()

    if (!isLibertyRole) return

    const intervalId = window.setInterval(() => {
      void refreshCounts()
    }, POLL_INTERVAL_MS)

    const handleFocus = () => {
      void refreshCounts()
    }
    window.addEventListener('focus', handleFocus)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', handleFocus)
    }
  }, [enabled, isLibertyRole, refreshCounts])

  const dismissTodoPrompt = useCallback(() => {
    setTodoPrompt(null)
  }, [])

  return { counts, todoPrompt, dismissTodoPrompt, refreshCounts }
}
